import { useState, useEffect } from 'react';
import { Users, Plus, Trash2, Shield, Mail, Crown, User, X, Check, AlertCircle } from 'lucide-react';
import getApiUrl from './utils/getApiUrl';

const API = getApiUrl();

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

export default function AdminUsersPage({ user, onBack }) {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState('user');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const loadEmails = async () => {
    try {
      const res = await authFetch(`${API}/api/auth/authorized-emails`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setEmails(data.emails || []);
      }
    } catch (e) {
      console.error('Erreur chargement emails:', e);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadEmails();
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (!newEmail || !newEmail.includes('@')) {
      setError('Email invalide');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('email', newEmail);
      formData.append('role', newRole);

      const res = await authFetch(`${API}/api/auth/authorized-emails`, {
        method: 'POST',
        body: formData,
        headers: getAuthHeaders()
      });

      const data = await res.json();

      if (res.ok) {
        setSuccess(`Email ${newEmail} autorisé avec succès`);
        setNewEmail('');
        setNewRole('user');
        setShowAdd(false);
        loadEmails();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.detail || 'Erreur lors de l\'ajout');
      }
    } catch (e) {
      setError('Erreur de connexion');
    }
  };

  const handleRemove = async (email) => {
    if (!window.confirm(`Retirer l'accès à ${email} ?`)) return;

    try {
      const res = await authFetch(`${API}/api/auth/authorized-emails/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (res.ok) {
        setSuccess(`Email ${email} retiré`);
        loadEmails();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const data = await res.json();
        setError(data.detail || 'Erreur lors de la suppression');
      }
    } catch (e) {
      setError('Erreur de connexion');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                <Shield className="w-7 h-7 text-amber-500" />
                Gestion des accès
              </h1>
              <p className="text-slate-400 mt-1">Gérez les utilisateurs autorisés à accéder au CRM</p>
            </div>
          </div>
          <button
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-white rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Ajouter un utilisateur
          </button>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-4 p-4 bg-red-500/20 border border-red-500/30 rounded-lg flex items-center gap-3 text-red-400">
            <AlertCircle className="w-5 h-5" />
            {error}
            <button onClick={() => setError(null)} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-green-500/20 border border-green-500/30 rounded-lg flex items-center gap-3 text-green-400">
            <Check className="w-5 h-5" />
            {success}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Users className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{emails.length}</p>
                <p className="text-slate-400 text-sm">Utilisateurs autorisés</p>
              </div>
            </div>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <Crown className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {emails.filter(e => e.role === 'admin').length}
                </p>
                <p className="text-slate-400 text-sm">Administrateurs</p>
              </div>
            </div>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <User className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {emails.filter(e => e.role === 'user').length}
                </p>
                <p className="text-slate-400 text-sm">Utilisateurs</p>
              </div>
            </div>
          </div>
        </div>

        {/* List */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-700/50">
            <h2 className="text-lg font-semibold text-white">Emails autorisés</h2>
          </div>
          
          {loading ? (
            <div className="p-8 text-center text-slate-400">Chargement...</div>
          ) : emails.length === 0 ? (
            <div className="p-8 text-center text-slate-400">
              Aucun utilisateur autorisé. Ajoutez-en un pour commencer.
            </div>
          ) : (
            <div className="divide-y divide-slate-700/50">
              {emails.map((item, idx) => (
                <div key={idx} className="px-6 py-4 flex items-center justify-between hover:bg-slate-700/30 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-lg ${item.role === 'admin' ? 'bg-amber-500/20' : 'bg-slate-700'}`}>
                      {item.role === 'admin' ? (
                        <Crown className="w-5 h-5 text-amber-400" />
                      ) : (
                        <User className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                    <div>
                      <p className="text-white font-medium">{item.email}</p>
                      <div className="flex items-center gap-3 text-sm">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          item.role === 'admin' 
                            ? 'bg-amber-500/20 text-amber-400' 
                            : 'bg-slate-700 text-slate-400'
                        }`}>
                          {item.role === 'admin' ? 'Admin' : 'Utilisateur'}
                        </span>
                        {item.added_by && (
                          <span className="text-slate-500">
                            Ajouté par {item.added_by}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemove(item.email)}
                    className="p-2 hover:bg-red-500/20 rounded-lg transition-colors group"
                    title="Retirer l'accès"
                  >
                    <Trash2 className="w-4 h-4 text-slate-400 group-hover:text-red-400" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal Add */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
              <h2 className="text-lg font-bold text-white">Ajouter un utilisateur</h2>
              <button onClick={() => setShowAdd(false)} className="p-2 hover:bg-slate-700 rounded-lg">
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            <form onSubmit={handleAdd} className="p-6 space-y-4">
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Email Google *</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="utilisateur@example.com"
                    className="w-full pl-10 pr-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-amber-500"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Rôle</label>
                <select
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-amber-500"
                >
                  <option value="user">Utilisateur</option>
                  <option value="admin">Administrateur</option>
                </select>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAdd(false)}
                  className="flex-1 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3 bg-amber-500 hover:bg-amber-400 text-white rounded-lg transition-colors"
                >
                  Ajouter
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
